import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import queryString from 'query-string';
import TapPageHead from '../../components/TapPageHead';
import { STUDENTS_INFO, LOAD_NEXT_STUDENTS } from '../../redux/actions/studentsActions';
import TapPageStudentItem from '../../components/TapPageStudentItem';
import TapPageStudentItemV2 from '../../components/TapPageStudentItemV2';
import Loader from '../../components/Loader';
import SocialProof from '../../components/account/SocialProof';
import { SET_SEARCH_TERM_HASH, RETURN_PREV_SEARCH } from '../../redux/actions/searchActions';
import { RETURN_PREV_FILTERS } from '../../redux/actions/filtersActions';
import {
    getCommonRoutePath,
    isEqualArrays,
    isGlobalVersionTwo,
    isInIframe,
    isMobileExperiment,
} from '../../helpers/Helpers';
import {
    groupTapFeedExperienceBasedQuestions,
    showExperienceBasedQuestions,
} from '../../helpers/ExperianceBasedQuestionsHelper';
import cn from 'classnames';

import { ACCOUNT, startRegistration } from '../../redux/actions/accountActions';
const RegistrationPopup = React.lazy(
    () => import(/* registration-popup */ '../../components/popups/RegistrationPopup')
);
const LoginPopup = React.lazy(() => import(/* login-popup */ '../../components/popups/LoginPopup'));
const ExperienceBasedQuestionsPopup = React.lazy(
    () => import(/* experienced-questions */ '../../components/popups/ExperienceBasedQuestionsPopup')
);
const MatchMePopup = React.lazy(() => import(/* match-me-popup */ './components/popups/MatchMePopup'));
const NoSearchResultPlaceholder = React.lazy(
    () => import(/* match-me-popup */ '../../components/NoSearchResultPlaceholder')
);
import { prepareSearchTerms } from '../../helpers/queryHelper';
import { showMatchMe } from '../../helpers/UserHelper';
import '../../assets/styles/custom/components/ambassador.scss';
import '../../assets/styles/custom/components/match_me_popup.scss';
import '../../assets/styles/custom/pages/ambassadors.scss';
import '../../assets/styles/custom/sharable/ambassador_and_profile_page.scss';
import '../../assets/styles/custom/sharable/ambassador_post_profile.scss';
import '../../assets/styles/libs/flag-icon.min.css';
import { Button, List } from 'antd';

import { MESSAGE_EVENT_KEYS } from 'src/common/messageEventKeys';

const TapPageChatToUs = () => {
    const dispatch = useDispatch();

    const { onlyOneScreen, navigateTo } = useSelector((state) => state.main);
    const {
        items: students,
        refreshStudentsData,
        nextLoading,
        total,
        limit,
        isLoading,
    } = useSelector((state) => state.students);
    const {
        titles: { social_proof: socialProof, social_proof_data: socialProofData, profile_title: profileTitle },
        universitiesById: universities,
        groupUniversityId,
        institution,
        university_name: universityName,
        filters,
        clientWidth,
    } = useSelector((state) => state.widget);
    const searchTerms = useSelector((state) => state.searchTerms);
    const filterTerms = useSelector((state) => state.filterTerms);
    const account = useSelector((state) => state.account);

    const [modalOffset, setModalOffset] = useState(0);
    const [matchModalIsOpened, setMatchModalIsOpened] = useState(false);

    const listenerRef = React.useRef({ students, nextLoading, total, limit, searchTerms, filterTerms });

    useEffect(() => {
        listenerRef.current = { students, nextLoading, total, limit, searchTerms, filterTerms };
    }, [students, nextLoading, total, limit, searchTerms, filterTerms]);

    useEffect(() => {
        const { bubbles, searchResultsHash } = searchTerms;

        // scrollToTop()
        if (window.top !== window.self && window.name !== 'tap-dashboard-iframe') {
            window.addEventListener('message', loadNextMessageEvent);
        } else {
            window.addEventListener('scroll', loadNextScrollEvent);
        }

        const queryTerms = queryString.parse(window.location.search); // take the ?search= from the URL if it exists

        if (refreshStudentsData || !isEqualArrays(bubbles, searchResultsHash.chatToUs)) {
            const terms = prepareSearchTerms(queryTerms);

            dispatch({
                type: STUDENTS_INFO,
                payload: {
                    search: [...terms, ...searchTerms.items, ...filterTerms.customFilters],
                    countryIds: filterTerms.countryIds,
                    profileTypeIds: filterTerms.profileTypeIds,
                    subjectIds: filterTerms.subjectIds,
                },
            });
        }

        return () => {
            window.removeEventListener('message', loadNextMessageEvent);
            window.removeEventListener('scroll', loadNextScrollEvent);

            dispatch({
                type: SET_SEARCH_TERM_HASH,
                payload: {
                    key: 'chatToUs',
                    searchTerm: searchTerms.bubbles,
                },
            });
        };
    }, []);

    const loadNextMessageEvent = useCallback(
        (message) => {
            const { students, nextLoading, total, limit, searchTerms, filterTerms } = listenerRef.current;
            if (message.data.scrollPosition) {
                setModalOffset(message.data.scrollPosition);
            } else if (
                !nextLoading &&
                students &&
                total > limit &&
                (message.data === MESSAGE_EVENT_KEYS.MESSAGE || message.data.name === MESSAGE_EVENT_KEYS.SCROLL)
            ) {
                dispatch({
                    type: LOAD_NEXT_STUDENTS,
                    payload: {
                        search: [...searchTerms.items, ...filterTerms.customFilters],
                        groupCountryIds: filterTerms.groupCountryId ? [filterTerms.groupCountryId] : null,
                        countryIds: filterTerms.countryIds,
                        profileTypeIds: filterTerms.profileTypeIds,
                        subjectIds: filterTerms.subjectIds,
                    },
                });
            }
        },
        [dispatch]
    );

    const loadNextScrollEvent = useCallback(() => {
        try {
            const { students, nextLoading, total, limit, searchTerms, filterTerms } = listenerRef.current;
            if (
                !nextLoading &&
                students &&
                total > limit &&
                window.innerHeight + window.scrollY + 300 >= document.body.offsetHeight
            ) {
                dispatch({
                    type: LOAD_NEXT_STUDENTS,
                    payload: {
                        search: [...searchTerms.items, ...filterTerms.customFilters],
                        groupCountryIds: filterTerms.groupCountryId ? [filterTerms.groupCountryId] : null,
                        countryIds: filterTerms.countryIds,
                        profileTypeIds: filterTerms.profileTypeIds,
                        subjectIds: filterTerms.subjectIds,
                    },
                });
            }
        } catch (err) {
            console.log(err);
        }
    }, [dispatch]);

    const searchUsers = useCallback(
        (options, getNext = () => {}) => {
            if (!options) {
                options = {
                    search: null,
                    random: false,
                };
            }

            dispatch({
                type: STUDENTS_INFO,
                payload: options,
                onNotFound: getNext,
            });
        },
        [dispatch]
    );

    const handleChatToMeClick = useCallback(
        (university, student) => {
            const { name, id } = student;
            const isGroupExperienceBasedQuestions = showExperienceBasedQuestions();

            if (isInIframe) {
                window.parent.postMessage('scrollPosition', '*');
            }

            if (isGroupExperienceBasedQuestions) {
                groupTapFeedExperienceBasedQuestions({ university, id, name, type: 'ambassador' });
            } else {
                if (institution?.isEnabledExperienceBasedQuestions) {
                    dispatch({
                        type: ACCOUNT.OPEN_EXPERIENCE_BASED_QUESTIONS_POPUP,
                        payload: { university, id, name },
                    });
                } else {
                    dispatch(startRegistration({ university, id, name, type: 'ambassador' }));
                }
            }
        },
        [groupUniversityId, institution]
    );

    const handleAskExperienceBasedQuestion = useCallback(
        ({ university, id, name, questionToAsk }) => {
            dispatch(startRegistration({ university, id, name, type: 'ambassador' }));
            dispatch({
                type: ACCOUNT.SAVE_EXPERIENCE_BASED_QUESTION,
                payload: { questionToAsk },
            });
        },
        [dispatch]
    );

    const handleSkipExperienceBasedQuestion = useCallback(
        ({ university, id, name }) => {
            dispatch(startRegistration({ university, id, name, type: 'ambassador' }));
        },
        [dispatch]
    );

    const matchMe = useCallback(() => {
        setMatchModalIsOpened(true);
    }, []);

    const matchMeClose = useCallback(
        (returnBackState = true) => {
            setMatchModalIsOpened(false);
            if (returnBackState) {
                dispatch({
                    type: RETURN_PREV_SEARCH,
                });
                dispatch({
                    type: RETURN_PREV_FILTERS,
                });
            }
        },
        [dispatch]
    );

    if (navigateTo && navigateTo !== '/chat-to-us') {
        return <Navigate replace to={`${getCommonRoutePath()}${navigateTo}`} />;
    }

    const pageBodyClassName = cn('page-body js-index', {
        'one-screen': onlyOneScreen,
    });

    const showMatchBlocks = showMatchMe();

    let studentsView = [];
    if (students && students.length) {
        const getChunkData = (student) => {
            if (isGlobalVersionTwo) {
                return (
                    <TapPageStudentItemV2
                        searchUsers={searchUsers.bind(this)}
                        key={student.id}
                        student={student}
                        groupUniversityId={groupUniversityId}
                        university={
                            institution ? institution : universities ? universities[student.university_id] : null
                        }
                        fromChatToUs={true}
                        onChatToMeClick={handleChatToMeClick}
                    />
                );
            } else {
                return (
                    <TapPageStudentItem
                        searchUsers={searchUsers.bind(this)}
                        key={student.id}
                        student={student}
                        groupUniversityId={groupUniversityId}
                        university={
                            institution ? institution : universities ? universities[student.university_id] : null
                        }
                        fromChatToUs={true}
                        onChatToMeClick={handleChatToMeClick}
                    />
                );
            }
        };
        if (showMatchBlocks) {
            if (clientWidth >= 900) {
                for (let i = 0; i < students.length; i += 3) {
                    studentsView.push(
                        <div key={i + '-match-1'} className="mod-match-me-row chats-row-3">
                            {getChunkData(students[i])}
                            {students[i + 1] ? getChunkData(students[i + 1]) : null}
                            {students[i + 2] ? getChunkData(students[i + 2]) : null}
                        </div>
                    );
                    if (filters.length > 0 && students[i + 3]) {
                        studentsView.push(
                            <Button
                                key={i + '-match-1-button'}
                                className="match-me-button chats-row-1"
                                onClick={matchMe}
                            >
                                Match me with an ambassador
                            </Button>
                        );
                    }
                }
            } else if (clientWidth < 900 && clientWidth > 600) {
                for (let i = 0; i < students.length; i += 2) {
                    studentsView.push(
                        <div key={i + '-match-2'} className="mod-match-me-row chats-row-2">
                            {getChunkData(students[i])}
                            {students[i + 1] ? getChunkData(students[i + 1]) : null}
                        </div>
                    );
                    if (filters.length > 0 && students[i + 2]) {
                        studentsView.push(
                            <Button
                                key={i + '-match-2-button'}
                                className="match-me-button chats-row-1"
                                onClick={matchMe}
                            >
                                Match me with an ambassador
                            </Button>
                        );
                    }
                }
            } else {
                for (let i = 0; i < students.length; i += 1) {
                    studentsView.push(
                        <div key={i + '-match-3'} className="mod-match-me-row chats-row-1">
                            {getChunkData(students[i])}
                        </div>
                    );
                    if (filters.length > 0 && students[i + 3] && i % 3 === 0) {
                        studentsView.push(
                            <Button
                                key={i + '-match-3-button'}
                                className="match-me-button chats-row-1"
                                onClick={matchMe}
                            >
                                Match me with an ambassador
                            </Button>
                        );
                    }
                }
            }
        } else {
            studentsView = students.map((student) => getChunkData(student));
        }
    }

    const isMobileEperment = isMobileExperiment();

    return (
        <div id="page-body" className={pageBodyClassName}>
            <section className="tap-page">
                {!isMobileEperment && ( // TDOO: remove mobile condition after experiment over
                    <TapPageHead
                        isLoading={isLoading}
                        searchUsers={searchUsers}
                        showEmptyPlaceholder={students && students.length ? false : true}
                    />
                )}

                {isLoading ? (
                    <div className="loader-block-stick">
                        <Loader />
                    </div>
                ) : (
                    <div className="widget-body">
                        <div className="body-container">
                            {students && students.length && !account.isAuthFromMobApp && !isMobileEperment ? ( // TDOO: remove mobile condition after experiment over
                                <div className="title-container">
                                    {!isGlobalVersionTwo && (
                                        <h2 className="section-header chat-section-header">
                                            {profileTitle || 'Chat with our ambassadors'}
                                        </h2>
                                    )}
                                    <SocialProof
                                        social_proof={socialProof}
                                        social_proof_data={socialProofData}
                                        uniName={universityName}
                                    />
                                </div>
                            ) : null}
                            {showMatchBlocks ? (
                                students && students.length > 0 ? (
                                    studentsView
                                ) : (
                                    <NoSearchResultPlaceholder />
                                )
                            ) : (
                                <>
                                    {students && students.length ? (
                                        <List className={'chats-row mod-page'}>{studentsView}</List>
                                    ) : (
                                        <NoSearchResultPlaceholder />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </section>

            <Suspense fallback={null}>
                <RegistrationPopup bodyStyles={{ marginTop: modalOffset }} />
            </Suspense>
            <Suspense fallback={null}>
                <LoginPopup bodyStyles={{ marginTop: modalOffset }} />
            </Suspense>
            <Suspense fallback={null}>
                <ExperienceBasedQuestionsPopup
                    bodyStyles={{ marginTop: modalOffset }}
                    handleAskExperienceBasedQuestion={handleAskExperienceBasedQuestion}
                    handleSkipExperienceBasedQuestion={handleSkipExperienceBasedQuestion}
                    maskClosable={true}
                />
            </Suspense>
            {matchModalIsOpened && (
                <Suspense fallback={null}>
                    <MatchMePopup
                        styles={{ marginTop: modalOffset }}
                        isOpened={matchModalIsOpened}
                        matchMeClose={matchMeClose}
                        searchUsers={searchUsers}
                    />
                </Suspense>
            )}
        </div>
    );
};

export default TapPageChatToUs;
